import { MouseEventHandler } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { DataEventProps } from "../../../../libraries/wpl/foundations/src/index";

type ButtonSizes = "large" | "small";
export type ButtonColors =
    | "darkblue"
    | "yellow"
    | "green"
    | "lightblue"
    | "white"
    | "gray"
    | "disabled";
export type ButtonTypes = "primary" | "secondary";
type InputTypes = "button" | "submit" | "reset";

interface ButtonStyle {
    textColor: string;
    buttonBackgroundColor: string;
    buttonHoverColor: string;
    buttonFocusColor: string;
    border: string;
}

interface ButtonStyles {
    primary: {
        darkblue: ButtonStyle;
        yellow: ButtonStyle;
        green: ButtonStyle;
        lightblue: ButtonStyle;
        gray: ButtonStyle;
        disabled: ButtonStyle;
    };
    secondary: {
        darkblue: ButtonStyle;
        yellow: ButtonStyle;
        green: ButtonStyle;
        white: ButtonStyle;
        disabled: ButtonStyle;
    };
}

const buttonStyles: ButtonStyles = {
    primary: {
        // default styles
        darkblue: {
            textColor: "#ffffff",
            buttonBackgroundColor: "#0052cc",
            buttonHoverColor: "#0047B3",
            buttonFocusColor: "#004099",
            border: "none"
        },
        yellow: {
            textColor: "#172b4d",
            buttonBackgroundColor: "#ffab00",
            buttonHoverColor: "#ff991f",
            buttonFocusColor: "#ff8b00",
            border: "none"
        },
        green: {
            textColor: "#172b4d",
            buttonBackgroundColor: "#79f2c0",
            buttonHoverColor: "#57d9a3",
            buttonFocusColor: "#36b37e",
            border: "none"
        },
        lightblue: {
            textColor: "#0052cc",
            buttonBackgroundColor: "#deebff",
            buttonHoverColor: "#B3D4FF",
            buttonFocusColor: "#4c9aff",
            border: "none"
        },
        gray: {
            textColor: "#172b4d",
            buttonBackgroundColor: "#ebecf0",
            buttonHoverColor: "#DFE1E6",
            buttonFocusColor: "#c1c7d0",
            border: "none"
        },
        disabled: {
            textColor: "#97a0af",
            buttonBackgroundColor: "#ebecf0",
            buttonHoverColor: "",
            buttonFocusColor: "",
            border: "none"
        }
    },
    secondary: {
        darkblue: {
            textColor: "#0052cc",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#ebecf0",
            buttonFocusColor: "#deebff",
            border: `0.063rem solid #0052cc`
        },
        yellow: {
            textColor: "#172b4d",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#f4f5f7",
            buttonFocusColor: "#FFF0B3",
            border: `0.063rem solid #ffab00`
        },
        green: {
            textColor: "#172b4d",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#f4f5f7",
            buttonFocusColor: "#e3fcef",
            border: `0.063rem solid #79f2c0`
        },
        white: {
            textColor: "#ffffff",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "rgba(255, 255, 255, 0.10)",
            buttonFocusColor: "rgba(255, 255, 255, 0.20)",
            border: `0.063rem solid #ffffff`
        },
        disabled: {
            textColor: "#97a0af",
            buttonBackgroundColor: "#ebecf0",
            buttonHoverColor: "",
            buttonFocusColor: "",
            border: "none"
        }
    }
};

const getButtonSize = (size: string | undefined): string => {
    if (size) {
        const buttonSize = size.toLowerCase();
        return buttonSize ? buttonSize : "large";
    }

    return "large";
};

const getButtonType = (type: string | undefined): string => {
    const buttonType = type?.toLowerCase();
    if (buttonType) {
        return buttonType;
    }

    return "primary";
};

const getButtonColor = (
    color: string | undefined,
    disabled: boolean | undefined
): string => {
    const buttonColor = color?.toLowerCase();
    if (disabled) {
        return "disabled";
    }
    if (buttonColor) {
        return buttonColor;
    }

    return "darkblue";
};

const getButtonStyles = (color: string, type: string): ButtonStyle => {
    const buttonTypeStyles = buttonStyles[type as keyof ButtonStyles];
    const styles: ButtonStyle =
        buttonTypeStyles[color as keyof typeof buttonTypeStyles];

    if (styles) {
        return styles;
    }

    // return darkblue primary styles if style was not found
    return buttonStyles.primary.darkblue;
};

export interface ButtonProps {
    text: string;
    type?: ButtonTypes;
    color?: ButtonColors;
    size?: ButtonSizes;
    onClick?: MouseEventHandler;
    url?: string;
    inputType?: InputTypes;
    disabled?: boolean;
    className?: string;
    dataEventProps?: DataEventProps;
}

export const Button = ({
    text,
    type,
    color,
    size,
    onClick,
    url,
    inputType,
    disabled,
    className,
    dataEventProps
}: ButtonProps) => {
    const buttonType: string = getButtonType(type);
    const buttonColor: string = getButtonColor(color, disabled);
    const {
        textColor,
        buttonBackgroundColor,
        buttonHoverColor,
        buttonFocusColor,
        border
    } = getButtonStyles(buttonColor, buttonType);
    const buttonSize: string = getButtonSize(size);
    const buttonHeight = buttonSize === "large" ? 2.5 : 2;
    const fontSize = buttonSize === "large" ? 1 : 0.875;
    const cursor = disabled ? "not-allowed" : "pointer";

    const serializedStyles = css`
        align-items: center;
        justify-content: center;
        background: ${buttonBackgroundColor};
        border-radius: 0.25rem;
        box-sizing: border-box;
        border: ${border};
        cursor: ${cursor};
        color: ${textColor};
        display: inline-flex;
        font-weight: 600;
        font-size: ${fontSize}rem;
        font-family: var(--bxp-charlie-text-font-family);
        height: ${buttonHeight}rem;
        line-height: 1.25rem;
        padding: 0 1rem;
        text-align: center;
        text-decoration: none;
        :hover,
        :focus {
            background: ${buttonHoverColor};
        }
        :active {
            background: ${buttonFocusColor};
        }
    `;

    const handleOnClick = (e: React.MouseEvent) => {
        if (disabled) {
            e.preventDefault();
        } else {
            onClick?.(e);
        }
    };

    if ((onClick || inputType) && !url) {
        return (
            <button
                className={className}
                css={serializedStyles}
                onClick={handleOnClick}
                disabled={disabled}
                type={inputType}
                {...dataEventProps}
            >
                {text}
            </button>
        );
    }

    return (
        <a
            className={className}
            css={serializedStyles}
            onClick={handleOnClick}
            href={url}
            {...dataEventProps}
        >
            {text}
        </a>
    );
};
